import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { trackFormConversion } from "@/lib/tracking";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Business-to-business lead form used by /for/:vertical pages.
 * Posts to the same /api/contact endpoint with vertical-tagged service_type so
 * leads land in one inbox but are still identifiable by vertical.
 */
const BusinessLeadForm = ({ vertical }) => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    volume: "",
    message: "",
  });

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.company || !form.volume) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      // Bundle the extra B2B fields into the shared /api/contact schema.
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_type: `${vertical.label} · ${form.volume}`,
        message: `[${vertical.formFields.companyLabel}] ${form.company}\n\n${form.message || "(No message provided)"}`,
      };
      const res = await axios.post(`${API}/contact`, payload);
      trackFormConversion(res.data?.id);
      setSuccess(true);
      toast.success("Request received. We'll be in touch shortly.");
      setForm({ name: "", company: "", email: "", phone: "", volume: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call 484-764-3392 directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid={`b2b-success-${vertical.slug}`}
        className="py-10 flex flex-col items-center text-center"
      >
        <div className="w-16 h-16 bg-[#D9480F] flex items-center justify-center border-2 border-black mb-6">
          <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
        <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-3">
          Request in.
        </h3>
        <p className="text-zinc-700 max-w-md">
          A real human at Blue Collar World will follow up within 1 business day.
          For anything urgent, call{" "}
          <a href="tel:4847643392" className="font-bold underline">484-764-3392</a>.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="mt-6 font-mono text-xs uppercase tracking-[0.25em] underline underline-offset-4 hover:text-[#D9480F]"
        >
          ← Send another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`b2b-form-${vertical.slug}`}
      className="space-y-5"
    >
      <div className="border-b-2 border-black pb-4 mb-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D9480F] font-bold mb-1">
          {vertical.code} · Business Inquiry
        </div>
        <div className="font-display uppercase text-2xl tracking-tight">
          {vertical.label}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
            Your Name *
          </label>
          <input
            data-testid={`b2b-name-${vertical.slug}`}
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium"
            placeholder="Jane Doe"
            required
          />
        </div>
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
            {vertical.formFields.companyLabel} *
          </label>
          <input
            data-testid={`b2b-company-${vertical.slug}`}
            type="text"
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium"
            placeholder="ACME Realty Group"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
            Email *
          </label>
          <input
            data-testid={`b2b-email-${vertical.slug}`}
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium"
            placeholder="you@company.com"
            required
          />
        </div>
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
            Phone *
          </label>
          <input
            data-testid={`b2b-phone-${vertical.slug}`}
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium"
            placeholder="412-555-0100"
            required
          />
        </div>
      </div>

      <div>
        <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
          {vertical.formFields.volumeLabel} *
        </label>
        <Select value={form.volume} onValueChange={(v) => update("volume", v)}>
          <SelectTrigger
            data-testid={`b2b-volume-${vertical.slug}`}
            className="w-full border-2 border-black rounded-none bg-[#F4F4F5] px-4 py-6 font-medium text-zinc-900 h-auto"
          >
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent className="rounded-none border-2 border-black">
            {vertical.formFields.volumeOptions.map((opt) => (
              <SelectItem
                key={opt}
                value={opt}
                className="rounded-none cursor-pointer focus:bg-[#F59E0B] focus:text-black"
              >
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
          Anything else we should know?
        </label>
        <textarea
          data-testid={`b2b-message-${vertical.slug}`}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          rows={4}
          className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium resize-none"
          placeholder="Property list, current pain points, timeline…"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid={`b2b-submit-${vertical.slug}`}
        className="w-full bg-[#D9480F] text-white px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#E8590C] transition-colors border-2 border-black disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Sending...
          </>
        ) : (
          <>Request Proposal →</>
        )}
      </button>

      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-center pt-2">
        We respond within 1 business day · No spam, ever
      </p>
    </form>
  );
};

export default BusinessLeadForm;
